import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Bank, Money } from '@phosphor-icons/react';
import API from '../../lib/api';
import { useAuth } from '../../lib/auth';

const PartnerEarnings = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState('');
  const [upi, setUpi] = useState(user?.upi_id || '');
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const r = await API.get('/partners/me/earnings'); setData(r.data); if (r.data.available >= r.data.min_cashout) setAmount(String(r.data.available)); } catch (e) { console.error('[partner] load earnings failed', e); } };
  useEffect(() => { load(); }, []);

  const req = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post('/partners/me/cashout', { amount: parseFloat(amount), upi_id: upi });
      toast.success('Cashout requested');
      setAmount('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-8 text-center">Loading…</div>;
  const canCashout = data.available >= data.min_cashout;

  return (
    <div className="min-h-screen bg-neutral-100">
      <header className="bg-white border-b-2 border-black">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center gap-3">
          <Link to="/partner" className="pk-chip" data-testid="earnings-back"><ArrowLeft size={12}/> Back</Link>
          <div className="font-display text-[18px]">Earnings & Cash-out</div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-4">
          <div className="pk-card p-4">
            <div className="text-[10px] tracking-[0.24em] uppercase text-neutral-500">This month ({data.window_label})</div>
            <div className="font-display text-[30px] font-mono mt-1">₹{data.month_earnings.toLocaleString('en-IN')}</div>
            <div className="text-[11px] text-neutral-500 mt-1">{data.month_count} delivered</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="pk-card p-3"><div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500">Lifetime</div><div className="font-mono font-bold">₹{data.lifetime.toLocaleString('en-IN')}</div></div>
            <div className="pk-card p-3"><div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500">Settled</div><div className="font-mono font-bold">₹{data.settled.toLocaleString('en-IN')}</div></div>
            <div className="pk-card p-3"><div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500">Pending</div><div className="font-mono font-bold">₹{data.pending.toLocaleString('en-IN')}</div></div>
            <div className="pk-card p-3" style={{ background: canCashout ? 'var(--pk-yellow)' : '#f5f5f5' }}><div className="text-[9px] tracking-[0.22em] uppercase">Available</div><div className="font-mono font-bold">₹{data.available.toLocaleString('en-IN')}</div></div>
          </div>

          <div className={`pk-card p-4 ${canCashout ? '' : 'opacity-70'}`}>
            <div className="flex items-center gap-2"><Bank size={18} weight="duotone"/><div className="font-bold text-[14px]">Request cash-out</div></div>
            <form onSubmit={req} className="mt-3 space-y-3">
              <div><label className="pk-label">Amount (min ₹{data.min_cashout})</label><input type="number" min={data.min_cashout} max={data.available} className="pk-input font-mono" value={amount} onChange={e => setAmount(e.target.value)} required data-testid="cashout-amount"/></div>
              <div><label className="pk-label">UPI ID</label><input className="pk-input font-mono" value={upi} onChange={e => setUpi(e.target.value)} data-testid="cashout-upi"/></div>
              <button type="submit" disabled={!canCashout || busy} className={`pk-btn w-full ${canCashout ? 'pk-btn-magenta' : ''}`} style={{ background: canCashout ? undefined : '#a3a3a3' }} data-testid="cashout-submit">
                <Money size={14}/> {canCashout ? 'Request Cash Out' : `Reach ₹${data.min_cashout} to enable`}
              </button>
              {!canCashout && <div className="text-[11px] text-neutral-500">Cash out unlocks at ₹{data.min_cashout.toLocaleString('en-IN')} available balance.</div>}
            </form>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="pk-card overflow-hidden">
            <div className="px-4 py-3 border-b-2 border-black flex items-center justify-between">
              <div className="font-display text-[16px]">Statement</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-neutral-500">Last 200 entries</div>
            </div>
            <table className="w-full text-[13px]">
              <thead className="bg-neutral-100">
                <tr className="text-left">
                  <th className="p-2 text-[9px] tracking-widest uppercase">When</th>
                  <th className="p-2 text-[9px] tracking-widest uppercase">Type</th>
                  <th className="p-2 text-[9px] tracking-widest uppercase">Ref</th>
                  <th className="p-2 text-[9px] tracking-widest uppercase text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.statement.map((s, i) => (
                  <tr key={i} className="border-t border-neutral-200">
                    <td className="p-2 font-mono text-[11px]">{s.when?.slice(0,16).replace('T', ' ')}</td>
                    <td className="p-2"><span className={`pk-chip text-[9px] ${s.type === 'credit' ? 'active' : ''}`}>{s.type}</span></td>
                    <td className="p-2 font-mono text-[11px]">
                      {s.ref}
                      <div className="text-neutral-400">{s.note}</div>
                      {s.transaction_ref && <div className="text-[10px] mt-1"><b>UTR:</b> <span className="text-neutral-600">{s.transaction_ref}</span></div>}
                      {s.settle_note && <div className="text-[10px] text-neutral-500 italic">"{s.settle_note}"</div>}
                    </td>
                    <td className={`p-2 text-right font-mono font-bold ${s.type === 'credit' ? 'text-green-700' : 'text-red-700'}`}>{s.type === 'credit' ? '+' : '-'}₹{(s.amount || 0).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
                {data.statement.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[13px] text-neutral-500">No transactions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PartnerEarnings;
